/**
 * Branded Float32Array types that distinguish per-loop (per face-corner) normals
 * from per-vertex (averaged) normals at compile time.
 *
 * Per-loop normals have one normal per topology corner (length = cornerCount * 3).
 * Per-vertex normals have one normal per unique vertex (length = vertexCount * 3).
 *
 * Mixing the two buffer types caused WASM out-of-bounds traps and silent
 * data corruption — the brands make such mismatches a compile-time error.
 *
 * Use `asLoopNormals()` / `asVertexNormals()` at system boundaries (WASM
 * returns, deserialization, structured-clone receives) where the raw
 * Float32Array is known to be the correct type.
 */

declare const __loopNormalBrand: unique symbol;
declare const __vertexNormalBrand: unique symbol;

/** Per-loop (per face-corner) normals — length = cornerCount * 3. */
export type LoopNormals = Float32Array & { readonly [__loopNormalBrand]: true };

/** Per-vertex (averaged) normals — length = vertexCount * 3. */
export type VertexNormals = Float32Array & {
  readonly [__vertexNormalBrand]: true;
};

// ---------------------------------------------------------------------------
// Cast helpers — use at system boundaries only
// ---------------------------------------------------------------------------

/** Assert a raw Float32Array is per-loop normals.
 *  In dev builds, validates the buffer length is divisible by 3. */
export function asLoopNormals(buf: Float32Array): LoopNormals {
  if (import.meta.env.DEV && buf.length % 3 !== 0) {
    throw new Error(
      `asLoopNormals: buffer length ${buf.length} is not divisible by 3`,
    );
  }
  return buf as LoopNormals;
}

/** Assert a raw Float32Array is per-vertex normals.
 *  In dev builds, validates the buffer length is divisible by 3. */
export function asVertexNormals(buf: Float32Array): VertexNormals {
  if (import.meta.env.DEV && buf.length % 3 !== 0) {
    throw new Error(
      `asVertexNormals: buffer length ${buf.length} is not divisible by 3`,
    );
  }
  return buf as VertexNormals;
}

// ---------------------------------------------------------------------------
// Clone helpers — preserve the brand through .slice()
// ---------------------------------------------------------------------------

export function cloneLoopNormals(n: LoopNormals): LoopNormals {
  return n.slice() as LoopNormals;
}

export function cloneVertexNormals(n: VertexNormals): VertexNormals {
  return n.slice() as VertexNormals;
}
